import { useState } from 'react';
import { ArrowLeft, Package, ShoppingCart, Loader2, MapPin, Sun, Clock, Moon, Mail } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { startCheckout } from '../../services/checkout/checkoutService';
import { trackFunnelEvent } from '../../services/analytics/funnelTracker';
import type { Protocol, ShippingAddress, ProtocolTiming } from '../../types';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const TIMING_LABELS: Record<ProtocolTiming, { label: string; icon: typeof Sun }> = {
  AM_ENERGY: { label: 'Morning', icon: Sun },
  MIDDAY_ABSORPTION: { label: 'Midday', icon: Clock },
  PM_RECOVERY: { label: 'Evening', icon: Moon },
};

interface FieldError {
  email?: string;
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

export function CheckoutReview() {
  const { state, dispatch } = useApp();
  const protocol = state.protocol as Protocol;
  const [emailInput, setEmailInput] = useState(state.checkoutEmail || '');

  const [address, setAddress] = useState<ShippingAddress>({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldError>({});
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Guard: if no protocol, redirect back
  if (!protocol) {
    dispatch({ type: 'SET_SCREEN', payload: 'protocol' });
    return null;
  }

  const handleBack = () => {
    dispatch({ type: 'SET_SCREEN', payload: 'protocol' });
  };

  const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const validateForm = (): boolean => {
    const errors: FieldError = {};
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(trimmedEmail)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!address.line1.trim()) errors.line1 = 'Street address is required';
    if (!address.city.trim()) errors.city = 'City is required';
    if (!address.state) errors.state = 'State is required';
    if (!address.postal_code.trim()) {
      errors.postal_code = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(address.postal_code.trim())) {
      errors.postal_code = 'Enter a valid 5-digit ZIP code';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCompletePurchase = async () => {
    if (!validateForm()) return;

    const trimmedEmail = emailInput.trim();

    // Persist email to app state so it's available downstream
    dispatch({ type: 'SET_CHECKOUT_EMAIL', payload: trimmedEmail });

    trackFunnelEvent({
      event: 'CHECKOUT_STARTED',
      metadata: { source: 'checkout_review', bundlePrice: protocol.bundlePrice || '' },
    });

    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const trimmedAddress: ShippingAddress = {
        line1: address.line1.trim(),
        line2: address.line2?.trim() || '',
        city: address.city.trim(),
        state: address.state,
        postal_code: address.postal_code.trim(),
        country: 'US',
      };

      const { sessionUrl } = await startCheckout(protocol, trimmedEmail, trimmedAddress);
      if (sessionUrl) {
        window.location.href = sessionUrl;
      }
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Checkout failed. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const updateField = (field: keyof ShippingAddress, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field as keyof FieldError]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Group supplements by timing for the order summary
  const timings: ProtocolTiming[] = ['AM_ENERGY', 'MIDDAY_ABSORPTION', 'PM_RECOVERY'];
  const groupedSupplements = timings
    .map((t) => ({
      timing: t,
      items: protocol.supplements.filter((s) => s.timing === t),
    }))
    .filter((g) => g.items.length > 0);

  // Parse price for display
  const priceStr = protocol.bundlePrice || '$0';
  const isSubscription = /\/mo/i.test(priceStr);
  const requiresProvider = protocol.tierRecommendation >= 2 &&
    protocol.supplements.some((s) => s.requiresRx);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#18181B]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button
            onClick={handleBack}
            className="text-zinc-400 hover:text-[#18181B] transition-colors"
            aria-label="Back to protocol"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="font-['Instrument_Serif'] text-lg font-bold">Review Your Order</h2>
            <p className="text-zinc-500 text-[13px] font-['DM_Sans']">Confirm details before checkout</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto font-['DM_Sans']">
        {/* Order Summary Card */}
        <div className="bg-white border border-zinc-200 rounded-[4px] p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Package size={16} className="text-[#E11D48]" />
            <h3 className="font-['Instrument_Serif'] text-base font-bold">
              {protocol.bundleName || 'Your Protocol'}
            </h3>
          </div>

          {/* Supplement list grouped by timing */}
          <div className="space-y-3 mb-4">
            {groupedSupplements.map(({ timing, items }) => {
              const config = TIMING_LABELS[timing];
              const Icon = config.icon;
              return (
                <div key={timing}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Icon size={13} className="text-zinc-400" />
                    <span className="text-[13px] font-medium text-zinc-500">{config.label}</span>
                  </div>
                  {items.map((s) => (
                    <div key={s.name} className="flex items-baseline justify-between py-1 pl-5">
                      <span className="text-[14px] text-[#18181B]">{s.name}</span>
                      <span className="text-[13px] text-zinc-500 ml-2 flex-shrink-0">{s.dosage}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Price breakdown */}
          <div className="border-t border-zinc-100 pt-3 space-y-1.5">
            <div className="flex justify-between text-[14px]">
              <span className="text-zinc-600">
                {protocol.bundleName || 'Protocol'} ({protocol.supplements.length} supplements)
              </span>
              <span className="font-medium">{priceStr}</span>
            </div>
            {requiresProvider && (
              <div className="flex justify-between text-[14px]">
                <span className="text-zinc-600">
                  Provider Consultation{isSubscription ? ' (one-time)' : ''}
                </span>
                <span className="font-medium">$49.00</span>
              </div>
            )}
            <div className="flex justify-between text-[14px] pt-1.5 border-t border-zinc-100 font-bold">
              <span>{isSubscription ? 'Monthly total' : 'Total'}</span>
              <span className="text-[#E11D48]">
                {requiresProvider && !isSubscription
                  ? (() => {
                      const match = priceStr.match(/\$?([\d,]+(?:\.\d{1,2})?)/);
                      if (!match) return priceStr;
                      const base = parseFloat(match[1].replace(',', ''));
                      return `$${(base + 49).toFixed(2)}`;
                    })()
                  : priceStr}
              </span>
            </div>
            {requiresProvider && isSubscription && (
              <p className="text-zinc-500 text-[12px]">
                + $49.00 provider consultation charged on first invoice only
              </p>
            )}
          </div>

          {/* Subscription context */}
          {isSubscription && (
            <div className="mt-3 bg-[#FAFAFA] border border-zinc-100 rounded-[4px] p-3">
              <p className="text-[13px] text-zinc-600">
                <span className="font-medium text-[#18181B]">Monthly subscription</span> — billed
                every month. Cancel or pause anytime from your{' '}
                <a href="/account/subscriptions" className="text-[#E11D48] underline underline-offset-2">
                  subscriptions page
                </a>.
              </p>
            </div>
          )}
        </div>

        {/* Email field */}
        <div className="bg-white border border-zinc-200 rounded-[4px] p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={16} className="text-[#E11D48]" />
            <h3 className="font-['Instrument_Serif'] text-base font-bold">Contact Email</h3>
          </div>
          <label htmlFor="checkout-email" className="text-[13px] font-medium text-zinc-600 block mb-1">
            Order confirmation will be sent here <span className="text-[#E11D48]">*</span>
          </label>
          <input
            id="checkout-email"
            type="email"
            placeholder="you@example.com"
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              if (fieldErrors.email) {
                setFieldErrors((prev) => ({ ...prev, email: undefined }));
              }
            }}
            className={`w-full px-3 py-2.5 text-[14px] rounded-[4px] border bg-white text-[#18181B] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E11D48]/30 focus:border-[#E11D48] transition-colors ${
              fieldErrors.email ? 'border-red-400' : 'border-zinc-200'
            }`}
          />
          {fieldErrors.email && (
            <p className="text-red-500 text-[12px] mt-1">{fieldErrors.email}</p>
          )}
        </div>

        {/* Shipping Address Form */}
        <div className="bg-white border border-zinc-200 rounded-[4px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={16} className="text-[#E11D48]" />
            <h3 className="font-['Instrument_Serif'] text-base font-bold">Shipping Address</h3>
          </div>
          <p className="text-[13px] text-zinc-500 mb-4">
            US shipping only. You can update this on the payment page.
          </p>

          <div className="space-y-3">
            {/* Street Address */}
            <div>
              <label htmlFor="addr-line1" className="text-[13px] font-medium text-zinc-600 block mb-1">
                Street Address <span className="text-[#E11D48]">*</span>
              </label>
              <input
                id="addr-line1"
                type="text"
                placeholder="123 Main St"
                value={address.line1}
                onChange={(e) => updateField('line1', e.target.value)}
                className={`w-full px-3 py-2.5 text-[14px] rounded-[4px] border bg-white text-[#18181B] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E11D48]/30 focus:border-[#E11D48] transition-colors ${
                  fieldErrors.line1 ? 'border-red-400' : 'border-zinc-200'
                }`}
              />
              {fieldErrors.line1 && (
                <p className="text-red-500 text-[12px] mt-1">{fieldErrors.line1}</p>
              )}
            </div>

            {/* Apt / Suite */}
            <div>
              <label htmlFor="addr-line2" className="text-[13px] font-medium text-zinc-600 block mb-1">
                Apt, Suite, Unit <span className="text-zinc-400">(optional)</span>
              </label>
              <input
                id="addr-line2"
                type="text"
                placeholder="Apt 4B"
                value={address.line2 || ''}
                onChange={(e) => updateField('line2', e.target.value)}
                className="w-full px-3 py-2.5 text-[14px] rounded-[4px] border border-zinc-200 bg-white text-[#18181B] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E11D48]/30 focus:border-[#E11D48] transition-colors"
              />
            </div>

            {/* City */}
            <div>
              <label htmlFor="addr-city" className="text-[13px] font-medium text-zinc-600 block mb-1">
                City <span className="text-[#E11D48]">*</span>
              </label>
              <input
                id="addr-city"
                type="text"
                placeholder="New York"
                value={address.city}
                onChange={(e) => updateField('city', e.target.value)}
                className={`w-full px-3 py-2.5 text-[14px] rounded-[4px] border bg-white text-[#18181B] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E11D48]/30 focus:border-[#E11D48] transition-colors ${
                  fieldErrors.city ? 'border-red-400' : 'border-zinc-200'
                }`}
              />
              {fieldErrors.city && (
                <p className="text-red-500 text-[12px] mt-1">{fieldErrors.city}</p>
              )}
            </div>

            {/* State + ZIP row */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor="addr-state" className="text-[13px] font-medium text-zinc-600 block mb-1">
                  State <span className="text-[#E11D48]">*</span>
                </label>
                <select
                  id="addr-state"
                  value={address.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  className={`w-full px-3 py-2.5 text-[14px] rounded-[4px] border bg-white text-[#18181B] focus:outline-none focus:ring-2 focus:ring-[#E11D48]/30 focus:border-[#E11D48] transition-colors ${
                    fieldErrors.state ? 'border-red-400' : 'border-zinc-200'
                  } ${!address.state ? 'text-zinc-400' : ''}`}
                >
                  <option value="" disabled>Select</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {fieldErrors.state && (
                  <p className="text-red-500 text-[12px] mt-1">{fieldErrors.state}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="addr-zip" className="text-[13px] font-medium text-zinc-600 block mb-1">
                  ZIP Code <span className="text-[#E11D48]">*</span>
                </label>
                <input
                  id="addr-zip"
                  type="text"
                  inputMode="numeric"
                  placeholder="10001"
                  maxLength={10}
                  value={address.postal_code}
                  onChange={(e) => updateField('postal_code', e.target.value)}
                  className={`w-full px-3 py-2.5 text-[14px] rounded-[4px] border bg-white text-[#18181B] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#E11D48]/30 focus:border-[#E11D48] transition-colors ${
                    fieldErrors.postal_code ? 'border-red-400' : 'border-zinc-200'
                  }`}
                />
                {fieldErrors.postal_code && (
                  <p className="text-red-500 text-[12px] mt-1">{fieldErrors.postal_code}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Complete Purchase Button */}
        <button
          onClick={handleCompletePurchase}
          disabled={checkoutLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-[#E11D48] text-white font-bold text-[15px] rounded-[4px] hover:bg-[#BE123C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {checkoutLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <ShoppingCart size={18} />
              {isSubscription ? 'Start Subscription' : 'Complete Purchase'}
            </>
          )}
        </button>

        {checkoutError && (
          <p className="text-red-500 text-[13px] mt-2 text-center">{checkoutError}</p>
        )}

        {/* Back link */}
        <button
          onClick={handleBack}
          className="w-full text-center text-[13px] text-zinc-500 hover:text-[#18181B] mt-3 py-2 transition-colors"
        >
          Back to Protocol
        </button>

        {/* Note about Stripe */}
        <p className="text-zinc-400 text-[12px] text-center mt-4 leading-relaxed">
          You will be redirected to Stripe for secure {isSubscription ? 'subscription setup' : 'payment processing'}.
          Shipping address can be confirmed or updated on the payment page.
        </p>
      </div>
    </div>
  );
}
